'use client';

import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { cn } from '@/app/utils';
import { EXAMPLE_MEDIA_PREVIEW_CONFIG, type ExampleMediaToolType } from '../_constants/example-media-preview';

interface ExampleMediaPreviewProps {
  tool: ExampleMediaToolType;
  className?: string;
}

export const ExampleMediaPreview: FC<ExampleMediaPreviewProps> = ({ tool, className }) => {
  const t = useTranslations();
  const config = EXAMPLE_MEDIA_PREVIEW_CONFIG[tool];

  if (!config) {
    return null;
  }

  const { imageUrl, videoUrl, poster, badgeKeys } = config;

  if (!imageUrl && !videoUrl) {
    return null;
  }

  const previewBadges =
    badgeKeys
      ?.map((badgeKey) => t(badgeKey))
      .filter(Boolean)
      .slice(0, 2) ?? [];
  const previewBadgeConfigs = [
    {
      key: 'top-left',
      label: previewBadges[0],
      className: 'left-3',
    },
    {
      key: 'offset-center',
      label: previewBadges[1],
      className: 'left-[calc(50%+12px)] bg-[#4C63FF]',
    },
  ].filter((badge): badge is { key: string; label: string; className: string } => Boolean(badge.label));

  return (
    <div
      className={cn(
        'relative flex h-[160px] w-full items-center justify-center overflow-hidden rounded-12 border border-system-white08 bg-system-white04',
        className,
      )}
    >
      {videoUrl ? (
        <video src={videoUrl} poster={poster} className="size-full object-cover" autoPlay loop muted playsInline>
          <track kind="captions" />
        </video>
      ) : (
        <img src={imageUrl} alt="demo" className="size-full object-cover" />
      )}

      {previewBadgeConfigs.map((badge) => (
        <span
          key={badge.key}
          className={cn(
            'pointer-events-none absolute top-2 z-[1] rounded-10 bg-black px-3 py-1 text-sm text-white leading-[1.4]',
            badge.className,
          )}
        >
          {badge.label}
        </span>
      ))}
    </div>
  );
};
